<template>
    <div v-if="course">
        <course-header :course="course" :show-image="showImage" :show-title="showTitle" :show-lastname-for-instructors="showLastnameForInstructors" :show-instructors="false" :booking-label-items="bookingLabelItems" class="mb-4" :textClass="dialog ? 'px-6' : null" :showClose="dialog" @close="$emit('close')" :discounted-price="courseDiscountedPrice" />

        <div :class="{'px-6 pb-3': dialog}">
            <div class="mt-3 mb-2">
                <div class="text-subtitle-1 font-weight-bold">{{$translate('About the course')}}</div>
                <div class="my-2 text-body-2"><div v-safe-html="showLongDescription ? course.longdescription : course.description" /> <a v-if="course.longdescription" @click="showLongDescription = !showLongDescription">{{$translate(showLongDescription ? 'Show less' : 'Read more')}}</a></div>
            </div>

            <div v-if="formattedStaffs && formattedStaffs.length" class="mt-3">
                <div class="text-subtitle-1 font-weight-bold">{{$translate('Instructors')}}</div>
                <div class="text-body-2">
                    <a href v-for="(staff,index) in formattedStaffs" :key="staff.id" @click.prevent="doShowStaffInfoDialog(staff)" class="primary--text z-no-underline">{{staff.displayName}}{{index != formattedStaffs.length-1 ? ' | ' : ''}}</a>
                </div>
            </div>

            <div v-if="course.occasions > 1" class="mt-4">
                <div class="occasions-toggle d-flex align-center" @click="showOccasions = !showOccasions">
                    <span class="text-subtitle-1 font-weight-bold flex-grow-1">{{$translate('Occasions')}} ({{course.occasions}})</span>
                    <v-icon>{{showOccasions ? 'mdi-chevron-up' : 'mdi-chevron-down'}}</v-icon>
                </div>
                <v-expand-transition>
                    <div v-show="showOccasions" class="mt-2">
                        <course-session-list
                            :course="course"
                            :show-lastname-for-instructors="showLastnameForInstructors"
                            @select-workout="openWorkoutDialog"
                            @select-resource="openMapDialog"
                        />
                    </div>
                </v-expand-transition>
            </div>

            <div v-else-if="course.occasions === 1" class="text--secondary mt-4">
                {{$translate('Course consists of one occasion')}}
            </div>

            <div v-if="includedProducts && includedProducts.length" class="mt-4">
                <div class="text-subtitle-1 font-weight-bold mb-2">{{$translate('Products included')}}</div>
                <v-expansion-panels accordion flat class="included-products-list">
                    <v-expansion-panel
                        v-for="product in includedProducts"
                        :key="product.id"
                        class="included-products-list__panel"
                    >
                        <v-expansion-panel-header class="included-products-list__header px-3">
                            <span class="font-weight-bold">{{product.name}}</span>
                        </v-expansion-panel-header>
                        <v-expansion-panel-content>
                            <div class="d-flex align-start" style="gap: 12px;">
                                <v-auto-img v-if="product.imagekey" :src="product.imagekey" width="64px" height="64px" max-width="64px" max-height="64px" class="rounded" />
                                <div class="text--secondary text-body-2">{{getProductSubtitle(product)}}</div>
                            </div>
                        </v-expansion-panel-content>
                    </v-expansion-panel>
                </v-expansion-panels>
            </div>

            <div class="d-flex flex-wrap mt-4" style="gap: 8px" v-if="course.tags && course.tags.length">
                <v-chip v-for="tag in course.tags" :key="tag.id" small label>{{tag.name}}</v-chip>
            </div>

            <div class="d-flex align-end mt-4 flex-wrap" style="gap: 8px;">
                <space-left-bar v-if="bookingStatusIsRelevant && showSpaceLeft" :space="course.space" :space-left="course.spaceLeft" />
                <div class="ml-auto flex-shrink-0">
                    <v-btn
                        v-if="canOnlyBookSelfAndBooked && canCancel(bookings[0].booking)"
                        color="error"
                        class="zoezi-button-thin"
                        @click="cancel(bookings[0].booking)"
                    >
                        {{$translate('CANCEL_BOOKING')}}
                    </v-btn>
                    <v-alert
                        v-else-if="canOnlyBookSelfAndBooked && !canCancel(bookings[0].booking)"
                        dense text type="success" border="left" icon="mdi-check-outline"
                    >
                        <strong>{{$translate('You are booked')}}</strong>
                    </v-alert>

                    <v-btn
                        v-else-if="canBook && !isLoggedIn"
                        color="primary"
                        class="zoezi-button-thin"
                        @click="book"
                    >
                        {{$translate('Book')}} {{(courseDiscountedPrice ? courseDiscountedPrice.final_price : course.price) | price}}
                    </v-btn>

                    <v-btn
                        v-else-if="canBook && isLoggedIn && bookableUsers.length === 0"
                        color="primary"
                        class="zoezi-button-thin"
                        @click="book"
                    >
                        {{$translate('Book')}} {{(courseDiscountedPrice ? courseDiscountedPrice.final_price : course.price) | price}}
                    </v-btn>

                    <template v-else-if="canBook">
                        <div class="d-flex" style="gap: 8px;">
                            <!-- Boka button, with dropdown if multiple people available -->
                            <template v-if="splitButtonUsers.length > 0 || canBookForOthers">
                                <div class="d-flex" v-if="splitButtonUsers.length > 1">
                                    <v-btn
                                        color="primary"
                                        class="zoezi-button-thin rounded-l-lg rounded-r-0"
                                        style="padding-top: 4px !important; padding-bottom: 4px !important; min-width: 90px; font-size: 1rem;"
                                        @click="book"
                                    >
                                        {{$translate('Book')}}
                                    </v-btn>
                                    <v-menu offset-x>
                                        <template v-slot:activator="{ on }">
                                            <v-btn v-on="on" color="primary" class="zoezi-button-thin rounded-r-lg rounded-l-0 px-2" style="min-width: 36px; padding-top: 4px !important; padding-bottom: 4px !important;">
                                                <v-icon>mdi-chevron-down</v-icon>
                                            </v-btn>
                                        </template>
                                        <v-list>
                                            <v-list-item v-for="u in splitButtonUsers" :key="u.id" @click="bookFor(u)">
                                                <v-list-item-title>{{u.name}}</v-list-item-title>
                                            </v-list-item>
                                        </v-list>
                                    </v-menu>
                                </div>
                                <v-btn
                                    v-else
                                    color="primary"
                                    class="zoezi-button-thin rounded-lg"
                                    style="padding-top: 4px !important; padding-bottom: 4px !important; min-width: 90px; font-size: 1rem;"
                                    @click="book"
                                >
                                    {{$translate('Book')}}
                                </v-btn>
                            </template>

                            <!-- Avboka button, with dropdown if multiple bookings -->
                            <div class="d-flex" v-if="cancellableBookings.length > 0">
                                <v-btn
                                    color="error"
                                    :class="cancellableBookings.length > 1 ? 'zoezi-button-thin rounded-l-lg rounded-r-0' : 'zoezi-button-thin rounded-lg'"
                                    style="padding-top: 4px !important; padding-bottom: 4px !important; min-width: 90px; font-size: 1rem;"
                                    @click="cancel(cancellableBookings[0].booking)"
                                >
                                    {{$translate('CANCEL_BOOKING')}}
                                </v-btn>
                                <v-menu offset-x v-if="cancellableBookings.length > 1">
                                    <template v-slot:activator="{ on }">
                                        <v-btn v-on="on" color="error" class="zoezi-button-thin rounded-r-lg rounded-l-0 px-2" style="min-width: 36px; padding-top: 4px !important; padding-bottom: 4px !important;">
                                            <v-icon>mdi-chevron-down</v-icon>
                                        </v-btn>
                                    </template>
                                    <v-list>
                                        <v-list-item v-for="bu in cancellableBookings" :key="bu.booking.id" @click="cancel(bu.booking)">
                                            <v-list-item-title>
                                                {{bu.booking.user_id === $store.state.user.id ? $translate('Cancel for me') : $translate('Cancel for {0}').format(bu.user.name)}}
                                            </v-list-item-title>
                                        </v-list-item>
                                    </v-list>
                                </v-menu>
                            </div>

                            <!-- Fully booked, nothing to book or cancel -->
                            <v-btn
                                v-if="splitButtonUsers.length === 0 && !canBookForOthers && cancellableBookings.length === 0"
                                disabled
                                color="primary"
                                class="zoezi-button-thin rounded-lg"
                                style="padding-top: 4px !important; padding-bottom: 4px !important; min-width: 90px; font-size: 1rem;"
                            >
                                {{$translate('Booked2')}}
                            </v-btn>
                        </div>
                    </template>

                    <!-- Course is full: show cancel and/or watch buttons side by side -->
                    <div class="d-flex" style="gap: 8px;" v-else-if="cancellableBookings.length > 0 || isWatching || canWatch">
                        <!-- Avboka (with per-person dropdown if multiple bookings) -->
                        <div class="d-flex" v-if="cancellableBookings.length > 0">
                            <v-btn
                                color="error"
                                :class="cancellableBookings.length > 1 ? 'zoezi-button-thin rounded-l-lg rounded-r-0' : 'zoezi-button-thin rounded-lg'"
                                style="padding-top: 4px !important; padding-bottom: 4px !important; min-width: 90px; font-size: 1rem;"
                                @click="cancel(cancellableBookings[0].booking)"
                            >
                                {{$translate('CANCEL_BOOKING')}}
                            </v-btn>
                            <v-menu offset-x v-if="cancellableBookings.length > 1">
                                <template v-slot:activator="{ on }">
                                    <v-btn v-on="on" color="error" class="zoezi-button-thin rounded-r-lg rounded-l-0 px-2" style="min-width: 36px; padding-top: 4px !important; padding-bottom: 4px !important;">
                                        <v-icon>mdi-chevron-down</v-icon>
                                    </v-btn>
                                </template>
                                <v-list>
                                    <v-list-item v-for="bu in cancellableBookings" :key="bu.booking.id" @click="cancel(bu.booking)">
                                        <v-list-item-title>
                                            {{bu.booking.user_id === $store.state.user.id ? $translate('Cancel for me') : $translate('Cancel for {0}').format(bu.user.name)}}
                                        </v-list-item-title>
                                    </v-list-item>
                                </v-list>
                            </v-menu>
                        </div>

                        <!-- Bevaka — shown alongside Avboka when the logged-in user is not
                             personally booked but others are (e.g. booked for family members) -->
                        <v-btn color="orange" outlined class="zoezi-button-thin" @click="showWatchDialog = true" v-if="isWatching">
                            {{$translate('Watching')}}
                        </v-btn>
                        <v-btn disabled outlined class="zoezi-button-thin" v-else-if="canWatch && !isLoggedIn">
                            {{$translate('Log in to watch the course')}}
                        </v-btn>
                        <v-btn color="primary" outlined class="zoezi-button-thin" @click="showWatchDialog = true" v-else-if="canWatch">
                            {{$translate('Watch')}}
                        </v-btn>
                    </div>
                    <v-alert v-else-if="isBlocked" dense text type="error" border="left" icon="mdi-alert-circle">
                        {{$translate('You are blocked and can not book')}}
                    </v-alert>
                    <v-alert v-else-if="ageBlocksWatching" dense text type="info" border="left" icon="mdi-alert-circle">
                        {{ purchaseAgeTooYoung ? $translate('Must be at least {0} years old').format(course.purchase_age.from) : $translate('Must be between {0} and {1} years').format(course.purchase_age.from, course.purchase_age.to) }}
                    </v-alert>
                </div>
            </div>

            <workout-dialog
                v-if="selectedWorkout"
                v-model="showWorkoutDialog"
                :workout="selectedWorkout"
                :show-lastname-for-instructors="showLastnameForInstructors"
            />
            <cancel-course-booking-dialog v-model="showCancelBooking" v-if="showCancelBooking" :course-booking="courseBookingToCancel" :cancel-enabled="true" />
            <staff-info-dialog v-model="showStaffInfoDialog" :staffId="showStaff.id" v-if="showStaff"/>
            <map-dialog v-model="showMapDialog" :resource="showMap" v-if="showMap" />

        </div>

        <bottom-dialog v-model="showBookingDialog" scrollable max-width="850px" :dialog="!$vuetify.breakpoint.mobile" v-if="showBookingDialog" :persistent="!almostdone">
            <v-card class="zoezi-bottom-sheet" tile elevation="0">
                <v-card-title class="headline align-start flex-column pa-2 primary primarytext--text">
                    <div class="font-weight-bold fill-width">
                        <span v-if="!almostdone" v-text="course.name" />
                        <v-btn icon @click="showBookingDialog = false" class="float-right" v-if="!checkoutLoading"><v-icon class="primary primarytext--text">mdi-close</v-icon></v-btn>
                    </div>
                </v-card-title>

                <v-card-text :class="{'px-2': !this.almostdone && $vuetify.breakpoint.mobile, 'px-0': this.almostdone, 'pb-0': this.almostdone }" class="z-no-x-scroll">
                    <template v-if="!almostdone">
                        <course-header :course="course" :show-image="false" class="pa-2" :discounted-price="courseDiscountedPrice" />
                    </template>

                    <checkout
                        ref="checkout"
                        :useCart="useCart"
                        class="overflow-y-auto"
                        :items="checkoutItems"
                        :expand-first-item="false"
                        :show-summary="true"
                        :show-back-link="false"
                        :show-payment-button="false"
                        :cancel-rules="cancelRules"
                        :force-done="almostdone"
                        :user-read-only="false"
                        @almostdone="almostdone = true"
                        @loading="checkoutLoading = $event"
                        @cost="cost = $event"
                        @readyToPay="readyToPay = $event"
                        @readyToPayExceptPaymentMethod="readyToPayExceptPaymentMethod = $event"
                        @changeNumberOfItems="changeNumberOfItems"
                        @changeItemUsers="changeItemUsers"
                        @done="showBookingDialog = false"
                        @close="showBookingDialog = false"
                        content-target="checkoutcontent"
                        :dialog="true"
                        :new-style-done-dialog="true"
                        @reservation-timeout="reservationTimeout"
                    >
                        <template #content>
                            <stepper-content v-model="currentStep" :steps="steps">
                                <template #account>
                                    <div><v-icon>mdi-account-check-outline</v-icon><span class="ml-2 font-weight-bold">{{$translate('Who are you who book?')}}</span></div>
                                    <div v-if="$store.state.user">
                                        <div class="pa-2 d-flex">
                                            <div class="d-flex align-center">
                                                <v-icon color="primary">mdi-account-star-outline</v-icon>
                                            </div>
                                            <div class="ml-2 flex-grow-1">
                                                <span class="font-weight-bold">{{$store.state.user.name}}</span><span @click.stop="$store.dispatch('logout')" class="z-clickable primary--text text-decoration-underline ml-4">{{$translate('Not you?')}}</span>
                                            </div>
                                        </div>
                                    </div>
                                    <identification v-else>
                                        <template #header>
                                            <div v-if="canBookForOthers" v-safe-html="$translate('Log in with <b>your personal</b> details here.')" />
                                            <div v-else>
                                                {{$translate('Choose which method you want to use to identify yourself.')}}
                                            </div>
                                        </template>
                                    </identification>
                                </template>
                                <template #questions>
                                    <v-alert dense text type="error" border="left" icon="mdi-information-outline" v-if="course.questions && course.questions.length && !questionsDone" >
                                        {{$translate('Answer the questions below to continue the booking')}}
                                    </v-alert>
                                    <template v-if="canBookForOthers">
                                        <div><v-icon>mdi-account-question-outline</v-icon><span class="ml-2 font-weight-bold">{{$translate('Who to book?')}}</span></div>

                                        <v-btn color="primary" :outlined="!newUserPrimaryColor" block class="zoezi-button-thin" @click="showAdduserDialog">
                                            <v-icon left :color="newUserPrimaryColor ? 'primarytext' : 'primary'">mdi-account-plus</v-icon>
                                            {{$translate('Add participant')}}
                                        </v-btn>

                                        <v-form v-model="questionsDone">
                                            <div class="z-rounded-box my-2" v-for="user in bookableUsers" :key="user.id">
                                                <div class="pa-2 d-flex">
                                                    <div class="d-flex align-center">
                                                        <v-simple-checkbox v-model="user.selected" color="primary" :ripple="false" :disabled="user.reserving || isBookedOrInCart(user) || user.isBlocked() || (typeof user.invalidPurchaseAge === 'function' && user.invalidPurchaseAge())" @input="userSelectedClicked(user, $event)" />
                                                    </div>
                                                    <div class="ml-2 flex-grow-1">
                                                        <span class="font-weight-bold">{{user.name}} {{user.me ? '(' + $translate('You') + ')' : ''}}</span>
                                                        <span v-if="isBooked(user)" class="red--text ml-4">{{$translate('Already booked')}}</span>
                                                        <span v-else-if="isInCart(user)" class="red--text ml-4">{{$translate('In the shopping cart')}}</span>
                                                        <span v-else-if="user.isBlocked()" class="red--text ml-4">{{ $translate('Blocked') }}</span>
                                                        <span v-else-if="typeof user.invalidPurchaseAge === 'function' && user.invalidPurchaseAge()" class="red--text ml-4">{{ $translate('Invalid age for purchase') }}</span>
                                                    </div>
                                                    <div class="ml-2 align-end" v-if="!user.me">
                                                        <span class="z-clickable primary--text text-decoration-underline ml-4" @click.stop="showChangeUserDialog(user)">{{$translate('Change')}}</span>
                                                    </div>
                                                    <div class="float-right" @click="user.expanded = !user.expanded" v-if="user.selected && course.questions && course.questions.length">
                                                        <v-icon>{{user.expanded ? 'mdi-chevron-up' : 'mdi-chevron-down'}}</v-icon>
                                                    </div>
                                                </div>

                                                <v-expand-transition v-if="course.questions && course.questions.length">
                                                    <div v-if="user.selected" class="px-2" v-show="user.expanded">
                                                        <form-field v-for="q in user.questions" :key="q.name" :field="q" star v-model="q.value" />

                                                        <div class="d-flex mb-1">
                                                            <v-btn color="primary" outlined class="flex-grow-1 zoezi-button-thin mr-1" @click="showAdduserDialog">
                                                                <v-icon left color="primary">mdi-account-plus</v-icon>
                                                                {{$translate('Additional participant')}}
                                                            </v-btn>
                                                            <v-btn color="primary" class="zoezi-button-thin ml-1" @click="user.expanded = false">{{$translate('Ready')}}</v-btn>
                                                        </div>
                                                    </div>
                                                </v-expand-transition>
                                            </div>
                                        </v-form>
                                    </template>
                                    <template v-else>
                                        <v-form v-model="questionsDone" v-if="meUser">
                                            <form-field v-for="q in meUser.questions" :key="q.name" :field="q" star v-model="q.value" />
                                        </v-form>
                                    </template>
                                </template>
                                <template #products>
                                    <v-expansion-panels multiple v-model="expandedProductPanels" accordion flat>

                                        <template v-if="additionalProducts.length">
                                            <v-expansion-panel class="mt-2" v-for="user in bookableUsers.filter(u => u.selected)" :key="user.id">
                                                <expansion-panel-header
                                                    icon="mdi-tag-plus-outline"
                                                    :title="canBookForOthers ? $translate('Add-on for {0}').format(user.name) : $translate('Add-on')"
                                                    :subtitle="$translate('Buy the products you need')"
                                                />
                                                <v-expansion-panel-content class="z-dense">
                                                    <additional-product-list :products="additionalProducts" :basket="user.basket" :show-added-banner="false" :total-basket="productsInBasket" />
                                                </v-expansion-panel-content>
                                            </v-expansion-panel>
                                        </template>
                                        <template v-if="includedProductsRequireVariant.length">
                                            <v-expansion-panel class="mt-2" v-for="user in bookableUsers.filter(u => u.selected)" :key="user.id">
                                                <expansion-panel-header
                                                    icon="mdi-tag-plus-outline"
                                                    :title="canBookForOthers ? $translate('Included products to {0}').format(user.name) : $translate('Included products')"
                                                    :subtitle="$translate('Choose variant for included products')"
                                                />
                                                <v-expansion-panel-content class="z-dense">
                                                    <v-card v-for="product in includedProductsRequireVariant" :key="product.id" class="d-flex mt-4 z-no-focus" height="65px" @click="showIncludedProductDialog = true; showingProduct = product; $nextTick(() => $refs.includedProductDialog.show())">
                                                        <v-auto-img :src="product.imagekey" width="100px" height="65px" max-width="100px" max-height="65px" style="border-bottom-left-radius: inherit; border-top-right-radius: 0" />
                                                        <div class="flex-grow-1 px-2">
                                                            <div><b>{{product.name}}</b></div>
                                                            <div style="height: 43px;overflow: hidden" v-safe-html="stripHtml(product.description)"></div>
                                                        </div>
                                                        <v-menu offset-y v-if="product.variants && product.variants.length > 1">
                                                            <template v-slot:activator="{ on }">
                                                                <v-btn v-on="on" color="primary" class="mr-2" :outlined="!!user.variantsForIncludedProducts[product.id]">
                                                                    {{user.variantsForIncludedProducts[product.id] ? 'Vald' : 'Välj variant'}}

                                                                    <v-icon>mdi-chevron-down</v-icon>
                                                                </v-btn>
                                                            </template>
                                                            <v-list>
                                                                <v-list-item-group v-model="user.variantsForIncludedProducts[product.id]">
                                                                    <v-list-item
                                                                        v-for="variant in product.variants"
                                                                        :key="variant.name"
                                                                        :value="variant.name"
                                                                        v-text="variant.name"
                                                                    />
                                                                </v-list-item-group>
                                                            </v-list>
                                                        </v-menu>
                                                    </v-card>
                                                </v-expansion-panel-content>
                                            </v-expansion-panel>
                                        </template>

                                        <v-expansion-panel v-if="includedProductsDontRequireVariant.length || course.occasions">
                                            <expansion-panel-header
                                                icon="mdi-calendar-month-outline"
                                                :title="$translate('This is included')"
                                                :subtitle="$translate('Products and occasions included')"
                                            />

                                            <v-expansion-panel-content class="z-dense">
                                                <div v-if="includedProductsDontRequireVariant.length" class="mb-4">
                                                    <div class="text-subtitle-1 font-weight-bold mb-n4">Produkter</div>

                                                    <v-card v-for="product in includedProductsDontRequireVariant" :key="product.id" class="d-flex mt-4 z-no-focus" height="65px" @click="showIncludedProductDialog = true; showingProduct = product; $nextTick(() => $refs.includedProductDialog.show())">
                                                        <v-auto-img :src="product.imagekey" width="100px" height="65px" max-width="100px" max-height="65px" style="border-bottom-left-radius: inherit; border-top-right-radius: 0" />
                                                        <div class="flex-grow-1 px-2">
                                                            <div><b>{{product.name}}</b></div>
                                                            <div style="height: 43px;overflow: hidden" v-safe-html="stripHtml(product.description)"></div>
                                                        </div>
                                                        <div class="d-flex align-center px-2">
                                                            <span class="primary--text font-weight-bold">{{$translate('Included')}}</span>
                                                        </div>
                                                    </v-card>
                                                </div>

                                                <div v-if="course.occasions">
                                                    <div class="text-subtitle-1 font-weight-bold">{{$translate('Training sessions')}}</div>
                                                    <div v-for="workout in course.workouts" :key="workout.id" class="d-flex">
                                                        <div class="d-flex align-center">
                                                            <v-icon small color="black">mdi-calendar-today</v-icon>
                                                        </div>
                                                        <div class="ml-2 flex-grow-1">
                                                            <div>{{workout.workoutType.name}}</div>
                                                            <div class="text--secondary text-uppercase" style="font-size: 12px">{{Date.formatFullShort(workout.startTime)}} {{Date.newFull(workout.startTime).hhmm()}}</div>
                                                        </div>
                                                    </div>
                                                </div>

                                            </v-expansion-panel-content>
                                        </v-expansion-panel>
                                    </v-expansion-panels>
                                </template>
                                <template #membership>
                                    <membership-checkout :items="checkoutItemsForMembershipCheckout" @buy="onBuyMembership" />
                                </template>
                                <template #checkout>
                                    <portal-target name="checkoutcontent" />
                                </template>
                            </stepper-content>
                        </template>
                    </checkout>
                </v-card-text>
                <v-card-actions v-if="!almostdone">
                    <stepper v-model="currentStep" :steps="steps" @cancel="showBookingDialog=false" ref="stepper" :show="!almostdone" :disabled="checkoutLoading" />
                </v-card-actions>
            </v-card>
        </bottom-dialog>

        <bottom-dialog v-model="showWatchDialog" scrollable max-width="500px" :dialog="!$vuetify.breakpoint.mobile" v-if="showWatchDialog">
            <v-card class="zoezi-bottom-sheet" tile elevation="0">
                <v-card-title class="headline align-start flex-column pa-2 primary primarytext--text">
                    <div class="font-weight-bold fill-width">
                        <span v-text="course.name" />
                        <v-btn icon @click="showWatchDialog = false" class="float-right"><v-icon class="primary primarytext--text">mdi-close</v-icon></v-btn>
                    </div>
                </v-card-title>

                <v-card-text :class="{'px-2': $vuetify.breakpoint.mobile }" class="z-no-x-scroll">
                    <template>
                        <course-header :course="course" :show-image="false" class="pa-2" :discounted-price="courseDiscountedPrice" />
                    </template>

                    <template v-if="$store.state.isLoggedIn">
                        <div class="d-flex">
                            <v-icon>mdi-account-question</v-icon>
                            <div class="ml-2 font-weight-bold">{{$translate('How do you want to be notified?')}}</div>
                        </div>
                        <template v-if="hasSms">
                            {{$translate('You can fill in both email address and phone number, but mark the method you prefer to use by ticking the box next to it. The contact method used is also depend on settings set by staff for watching.')}}
                        </template>

                        <v-form v-model="watch.formValid">
                            <form-field :field="watch.firstname" star v-model="watch.firstname.value" />
                            <form-field :field="watch.lastname" star v-model="watch.lastname.value" />

                            <template v-if="hasSms">
                                <div class="d-flex">
                                    <v-simple-checkbox v-model="watch.sendMail" color="primary" :ripple="false" />
                                    <form-field class="ml-2" :field="watch.mail" star v-model="watch.mail.value" />
                                </div>
                                <div class="d-flex">
                                    <v-simple-checkbox v-model="watch.sendSms" color="primary" :ripple="false" />
                                    <form-field class="ml-2" :field="watch.phone" star v-model="watch.phone.value" />
                                </div>
                            </template>
                            <form-field v-else :field="watch.mail" star v-model="watch.mail.value" />
                        </v-form>
                    </template>
                    <identification v-else
                                    :title="$translate('The activity is fully booked, but you can monitor available spots by identifying yourself below. If you don\'t have an account, one will be created automatically.')"
                    />

                </v-card-text>
                <v-card-actions>
                    <template v-if="$store.state.isLoggedIn">
                        <template v-if="isWatching">
                            <v-btn style="width: 50%" outlined color="error" class="zoezi-button-thin" @click="stopWatching">
                                {{$translate('Stop watching')}}
                            </v-btn>
                            <v-btn style="width: 50%" color="primary" class="zoezi-button-thin"  @click="startWatching" :disabled="!watch.formValid" :loading="watch.loading">
                                {{$translate('Save')}}
                            </v-btn>
                        </template>
                        <template v-else>
                            <v-btn style="width: 50%" outlined color="primary" class="zoezi-button-thin" @click="showWatchDialog = false">
                                {{$translate('Cancel')}}
                            </v-btn>
                            <v-btn style="width: 50%" color="primary" class="zoezi-button-thin"  @click="startWatching" :disabled="!watch.formValid" :loading="watch.loading">
                                {{$translate('Watch')}}
                            </v-btn>
                        </template>
                    </template>
                    <v-btn v-else block outlined color="primary" class="zoezi-button-thin"  @click="showWatchDialog = false">
                        <div class="ma-1 fill-width text-center">
                            {{$translate('Cancel')}}
                        </div>
                    </v-btn>
                </v-card-actions>
            </v-card>
        </bottom-dialog>
        <watch-dialog
            v-model="showWatchDialog"
            :entity="course"
            :type="'course'"
            :infoComponentProps="{ course: course, showImage: false }"
            :infoComponent="CourseHeader"
            @close="closeWatchDialog"
            v-if="showWatchDialog"
        ></watch-dialog>

        <product-dialog
            v-model="showIncludedProductDialog"
            ref="includedProductDialog"
            :product="showingProduct"
            :show-count="false"
            :show-buy-button="false"
        />
    </div>
    <div v-else-if="(courseId && !courseLoadingFailed) || forceLoading" class="d-flex align-center flex-column">
        <div class="flex-grow-1 text-center text-h5">{{$translate('The course is loading...')}}</div>
        <v-progress-circular
            indeterminate
            color="primary"
        ></v-progress-circular>
    </div>
    <div v-else class="d-flex align-center flex-column">
        <v-card flat>
            <v-card-title class="headline primarytext--text primary">
                {{$translate('Could not find the course')}}
                <v-spacer></v-spacer>
                <v-btn icon @click="$emit('close')" class="primarytext--text">
                    <v-icon>mdi-close</v-icon>
                </v-btn>
            </v-card-title>
            <v-card-text>
                {{$translate('The course you are trying to find does not appear to exist, please try a different url, or go and check out our entire range to see if you can find something that suits you.')}}
            </v-card-text>
        </v-card>
    </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { stripHtml, formatPrice, getName, RelativeTime, getRandomId, getCartItems, formatStaffName } from '../util.js'
import BottomDialog from '../components/BottomDialog.vue'
import FormField from '../components/FormField.vue'
import CourseHeader from '../components/CourseHeader.vue'
import Checkout from '../views/Checkout.vue'
import Stepper from '../views/Stepper.vue'
import StepperContent from '../views/StepperContent.vue'
import ExpansionPanelHeader from '../views/ExpansionPanelHeader.vue'
import ProductDialog from '../components/ProductDialog.vue'
import AdditionalProductList from '../views/AdditionalProductList.vue'
import Identification from '../views/Identification.vue'
import CancelCourseBookingDialog from './CancelCourseBookingDialog.vue'
import MembershipCheckout from './MembershipCheckout.vue'
import WatchDialog from '../views/WatchDialog.vue'
import SpaceLeftBar from '../components/SpaceLeftBar.vue'
import CourseSessionList from '../components/CourseSessionList.vue'
import WorkoutDialog from '../components/WorkoutDialog.vue'
import MapDialog from '../components/MapDialog.vue'
import StaffInfoDialog from '../components/StaffInfoDialog.vue'
import Vue from 'vue'
import {debounce} from 'lodash';
import * as Sentry from '@sentry/browser'
import priceRulePricing from '../mixins/priceRulePricing'

export default {
    name: 'zoezi-coursebooking',
    mixins: [priceRulePricing],

    zoezi: {
        title: 'Course booking',
        icon: 'mdi-book',
        addon: 'Courses'
    },

    props: {
        courseId: {
            title: 'Course',
            type: Number,
            fulltype: 'select',
            typeinfo: {
                multiple: false,
                text: 'name',
                value: 'id',
                getItems: () => window.$zoeziapi.get('/api/public/course/get',{bookable:true}).then(courses => courses.map(course => ({id: course.id, name: course.name}))),
                selectFirst: true
            }
        },
        showImage: {
            title: 'Show image',
            type: Boolean,
            default: true
        },
        showTitle: {
            title: 'Show title',
            type: Boolean,
            default: true
        },
        showSpaceLeft: {
            title: 'Show number of places left',
            type: Boolean,
            default: true
        },
        dialog: {
            type: Boolean,
            default: false
        },
        skipInfo: {
            type: Boolean,
            default: false
        },
        forceLoading: {
            type: Boolean,
            default: false
        },
        useCart: {
            title: 'Use shopping cart',
            type: Boolean,
            default: true
        },
        cartName: {
            title: 'Name of shopping cart',
            type: String,
            default: ''
        },
        showLastnameForInstructors: {
            type: Boolean,
            default: false
        }
    },

    components: {
        BottomDialog,
        FormField,
        CourseHeader,
        Checkout,
        Stepper,
        StepperContent,
        ExpansionPanelHeader,
        ProductDialog,
        AdditionalProductList,
        Identification,
        CancelCourseBookingDialog,
        MembershipCheckout,
        WatchDialog,
        SpaceLeftBar,
        CourseSessionList,
        WorkoutDialog,
        StaffInfoDialog,
        MapDialog
    },

    data: () => ({
        currentStep: 0,
        almostdone: false,
        course: null,
        products: null,
        showBookingDialog: false,
        questionsDone: false,
        expandedQuestionPanels: [0],
        expandedProductPanels: [0,1,2],
        showingProduct: null,
        checkoutLoading: false,
        cost: null,
        readyToPay: false,
        readyToPayExceptPaymentMethod: false,
        bookMe: true,

        users: [
        ],
        unsubscribe: null,
        showCancelBooking: false,
        courseBookingToCancel: null,
        showWatchDialog: false,
        showWorkoutDialog: false,
        selectedWorkout: null,
        showStaffInfoDialog: false,
        showStaff: null,
        showMapDialog: false,
        showMap: null,
        bookForUserId: null,
        watch: {
            firstname: {type: 'text', title: 'First name', required: true, readonly: false, value: ''},
            lastname: {type: 'text', title: 'Last name', required: true, readonly: false, value: ''},
            phone: {type: 'phone', title: 'Mobile phone number', icon: 'mdi-cellphone-iphone', required: true, readonly: false, value: ''},
            mail: {type: 'email', title: 'E-mail', required: true, icon: 'mdi-email', readonly: false, value: ''},
            sendMail: true,
            sendSms: true,
            loading: false,
            formValid: false
        },
        childRegisterFields: [],
        courseLoadingFailed: false,
        showLongDescription: false,
        showOccasions: false,
        showIncludedProductDialog: false,
        bookingId: getRandomId(8), // Used to identify the booking in the cart (tmp_id)
        CourseHeader: CourseHeader
    }),
    watch: {
        courseId: {
            immediate: true,
            handler(id) {
                if (id) {
                    this.getCourse();
                }
            }
        },
        canBook: {
            immediate: true,
            handler(canBook) {
                if (canBook && this.skipInfo) {
                    this.book();
                }
            }
        },
        'watch.sendMail'(sendMail) {
            this.watch.mail.required = sendMail;
            this.watch.mail.readonly = !sendMail;
        },
        'watch.sendSms'(sendSms) {
            this.watch.phone.required = sendSms;
            this.watch.phone.readonly = !sendSms;
        },
        course() {
            if (!this.course) {
                return;
            }
            this.updateUsers();

            if (this.products) {
                return;
            }

            try {
                let ecommerceTrackingItem = {
                    id: 'c' + this.course.id,
                    name: this.course.name,
                    price: this.course.price,
                    vat: this.course.vat,
                };
                this.$ecommercetracking.viewItem(ecommerceTrackingItem);
            } catch(error) {
                Sentry.captureException(error);
            }

            let a = this.course.additional_products || [];

            if (a.length == 0 && (this.course.included_products || []).length == 0) {
                return;
            }

            let ids = a.concat(this.course.included_products);
            this.$store.dispatch('loadProducts').then(allProducts => {
                this.products = allProducts.filter(p => ids.includes(p.id));
                this.products.forEach(x => {
                    x.variants = x.variants.sort((a,b) => a.name.localeCompare(b.name));
                });
                this.updateUsers();
            });
        },
        showBookingDialog(newVal, oldVal) {
            if (!newVal && oldVal && this.skipInfo) {
                this.$emit('close');
            }
            if (this.showBookingDialog) {
                this.almostdone = false;
                let me = this.users.find(user => user.me);
                if (me && !this.bookForUserId && (this.course.canBookFor == 'self' || this.course.canBookFor == 'self+others')) {
                    if (!(typeof me.invalidPurchaseAge === 'function' && me.invalidPurchaseAge())) {
                        me.selected = true;
                        this.updateUsers();
                    }
                }
                this.bookForUserId = null;
            } else {
                this.unreserveAll();
                this.clearAnswersAndBasket();
            }
        },
        showWatchDialog() {
            let user = this.$store.state.user;
            this.watch.firstname.value = user?.firstname;
            this.watch.lastname.value = user?.lastname;
            this.watch.mail.value = user?.mail;
            this.watch.phone.value = user?.phone;

            if (this.watchInfo) {
                this.watch.sendMail = this.watchInfo.mail;
                this.watch.sendSms = this.watchInfo.sms;
            } else {
                this.watch.sendMail = !!this.watch.mail.value;
                this.watch.sendSms = !!this.watch.phone.value;
            }
        },
        '$store.state.isLoggedIn': {
            immediate: true,
            handler(isLoggedIn) {
                if (isLoggedIn && this.showBookingDialog) {
                    if (this.$store.state.user.isBlocked('block_course')) {
                        this.showBookingDialog = false;
                        return;
                    }
                    if (this.currentStep == 0) {
                        this.currentStep++;
                    }
                }

                if (isLoggedIn && this.$store.state.user && this.$store.state.user.canBookFor) {
                    let bookSelf = this.course?.canBookFor == 'self' || this.course?.canBookFor == 'self+others';
                    let purchaseAge = this.course?.purchase_age;
                    this.users = this.$store.state.user.canBookFor.map((user,userindex) => ({
                        id: user.id,
                        name: user.name,
                        selected: this.$store.state.user.id == user.id && bookSelf && !(purchaseAge && (!user.birthdate || Date.getAge(user.birthdate) < Number(purchaseAge.from) || Date.getAge(user.birthdate) > Number(purchaseAge.to))),
                        me: this.$store.state.user.id == user.id,
                        reservation: null,
                        reserving: false,
                        expanded: userindex == 0,
                        membership: null,
                        isBlocked: () => this.$store.state.user.canBookFor.find(x => x.id == user.id)?.isBlocked('block_course'),
                        invalidPurchaseAge: () => {
                            let pa = this.course?.purchase_age;
                            if (!pa) return false;
                            if (!user.birthdate) return true;
                            let age = Date.getAge(user.birthdate);
                            return age < Number(pa.from) || age > Number(pa.to);
                        }
                    }));
                    this.updateUsers();
                }
                if (isLoggedIn && this.$store.state.user) {
                    this.watch.firstname.value = this.$store.state.user.firstname;
                    this.watch.lastname.value = this.$store.state.user.lastname;
                    this.watch.mail.value = this.$store.state.user.mail;
                    this.watch.phone.value = this.$store.state.user.phone;
                    this.watch.sendMail = !!this.$store.state.user.mail;
                    this.watch.sendSms = !!this.$store.state.user.phone;
                }
            }
        },
        bookings() {
            this.updateUsers();
        }
    },
    computed: {
        ...mapGetters(['selectedSiteId', 'isSiteIdValidForCart', 'hasModule']),
        courseDiscountedPrice() {
            if (!this.course || !this.course.id) return null;
            const startDate = this.course.startTime ? this.course.startTime.yyyymmdd() : null;
            const pr = this.priceRulePriceForCourse(this.course.id, startDate);
            return (pr && pr.has_discount) ? pr : null;
        },
        startDateString() {
            if (!this.course) {
                return null;
            }

            let d = Date.newFull(this.course.startTime);
            let lc = (x) => this.$store.state.language == 'sv' ? x.toLowerCase() : x;
            return lc(this.$translate(d.getWeekDayString())) + ' ' + d.getDate() + ' ' + lc(this.$translate(d.getMonthName())) + ' ' + d.getFullYear();
        },
        additionalProducts() {
            if (!this.products || !this.course) {
                return [];
            }

            let a = this.course.additional_products || [];
            return a.map(product_id => this.products.find(product => product.id == product_id)).filter(x => x);
        },
        includedProducts() {
            if (!this.products || !this.course) {
                return [];
            }

            let i = this.course.included_products || [];
            return i.map(product_id => this.products.find(product => product.id == product_id)).filter(x => x);
        },
        includedProductsDontRequireVariant() {
            return this.includedProducts.filter(x => !(x.variants && x.variants.length > 1));
        },
        includedProductsRequireVariant() {
            return this.includedProducts.filter(x => x.variants && x.variants.length > 1);
        },
        includedProductsHasVariantSet() {
            return !this.includedProductsRequireVariant.find(product => this.bookableUsers.filter(u => u.selected).find(u => !u.variantsForIncludedProducts[product.id]));
        },
        checkoutItems() {
            if (!this.course) {
                return null;
            }
            let course_id = this.course.id;

            let items = [];

            this.users.filter(user => user.selected && !this.isInCart(user)).forEach(user => {
                let answers = {};
                Object.values(user.questions).forEach(q => answers[q.name] = q.value)
                let tmp_id = this.bookingId + '-' + user.id;
                let site_id = this.selectedSiteId;
                if (this.course.sites.length && !this.course.sites.includes(site_id)) site_id = this.course.sites[0];
                let itemPrice = this.courseDiscountedPrice ? this.courseDiscountedPrice.final_price : this.course.price;
                items.push({count: 1, course_id, price: itemPrice, product_id: this.course.product_id, answers, user_id: user.id, tmp_id, reservation: user.reservation, variants_for_included_products: user.variantsForIncludedProducts, site_id})
                this.additionalProducts.forEach(product => {
                    let b = user.basket[product.id];
                    b.forEach(bi => {
                        if (bi.count) {
                            const baseItem = {
                                count: bi.count,
                                course_id,
                                price: product.price,
                                product_id: product.id,
                                variant: bi.variant,
                                tmp_id,
                                site_id
                            };

                            if (['trainingcard', 'membership'].includes(product.type)) {
                                // Training cards and memberships must always be tied to a specific user.
                                // Keep track of who each card should be connected to.
                                const assignedUsers = bi.users && bi.users.length
                                    ? bi.users.concat()
                                    : new Array(bi.count).fill(user.id);
                                baseItem.users = assignedUsers;
                                baseItem.user_id = user.id;
                            } else {
                                // Normal products (e.g. coffee, T‑shirts) are just linked via user_id.
                                baseItem.user_id = user.id;
                            }

                            items.push(baseItem);
                        }
                    })
                })

                if (user.membership) {
                    items.push({count: 1, users: [user.id], course_id, product_id: user.membership.id, product: user.membership, site_id});
                }
            });

            return items;
        },
        checkoutItemsForMembershipCheckout() {
            let memberships = this.users.filter(user => user.selected && user.membership && !this.isInCart(user)).map(u => ({
                count: 1,
                users: [u.id],
                course_id: this.course.id,
                product: u.membership
            }))
            return [{
                product: {requireMembership: this.course.requireMembership, startDate: Date.newFull(this.course.startTime), endDate: Date.newFull(this.course.endTime)},
                users: this.users.filter(user => user.selected && !this.isInCart(user)).map(user => user.id)
            }].concat(memberships);
        },
        bookings() {
            if (!this.$store.state.user) {
                return [];
            }

            let bookings = this.course && this.$store.state?.bookings?.coursebookings?.filter(cb => cb.course_id == this.course.id);
            if (bookings) {
                return bookings.map(booking => {
                    let user = this.$store.state.user.canBookFor.find(user => user.id == booking.user_id);
                    return {booking, user};
                }).filter(x => !!x.user);
            }

            return [];
        },
        steps() {
            let needQuestions = (this.course.questions && this.course.questions.length) || this.canBookForOthers;
            let showProducts = this.additionalProducts.length || this.includedProductsRequireVariant.length;
            let showMemberships = this.$booking.isMembershipRequired(this.checkoutItemsForMembershipCheckout);
            let mcomplete = !this.$booking.isMembershipStillRequired(this.checkoutItemsForMembershipCheckout);
            let cartDisabled = !this.readyToPayExceptPaymentMethod;
            let extraButton = this.useCart && {label: this.$translate('Add to cart and continue shopping'), clicked:this.addToCart, disabled: cartDisabled };
            const pay = this.$translate('Pay');

            return [
                {title: this.$translate('Account'), id: 'account', complete: this.$store.state.isLoggedIn},
                needQuestions && {title: this.$translate('Questions'), id: 'questions', complete: !!this.checkoutItems.length && this.questionsDone && !this.invalidPurchaseAge},
                showProducts && {title: this.$translate('Add-ons'), id: 'products', complete: this.includedProductsHasVariantSet},
                showMemberships && {title: this.$translate('Membership'), id: 'membership', complete: mcomplete, nextTitle: this.$translate(mcomplete ? 'Continue' : 'Choose membership')},
                {title: this.$translate('Complete'), id: 'checkout', nextTitle: this.cost > 0 ? `${pay} ${formatPrice(this.cost)}` : this.$translate('Confirm booking'), nextColor: '#7bbd1b', nextDark: true, nextClicked: this.pay, nextLoading: this.checkoutLoading, complete: this.readyToPay, extraButton }
            ].filter(x => x);
        },
        productsInBasket() {
            let res = [];
            this.additionalProducts.forEach(product => {
                this.users.filter(user => user.selected).forEach(user => {
                    let b = user.basket[product.id];
                    b.forEach(bi => {
                        if (bi.count) {
                            let r = res.find(x => x.product_id == product.id && x.variant == bi.variant);
                            if (r) {
                                r.count += bi.count;
                            } else {
                                res.push({count: bi.count, product_id: product.id, variant: bi.variant})
                            }
                        }
                    })
                })
            })

            return res;
        },
        cancelRules() {
            if (!this.course) {
                return null;
            }

            let lastCancelTime = this.course.canCancel && this.course.lastCancelTime ? new RelativeTime(this.course.lastCancelTime).getDate(this.course.startTime) : null;

            return {
                canCancel: this.course.canCancel,
                lastCancelTime,
                rules: this.course.cancelRules
            }
        },
        meUser() {
            return this.users.find(user => user.me);
        },
        canOnlyBookSelfAndBooked() {
            return this.course && this.course.canBookFor == 'self' && this.$store.state.user && this.bookings.find(b => b.user.id == this.$store.state.user.id) && this.bookings.length == 1;
        },
        bookingStatusIsRelevant() {
            return this.course.lastBookingTime > this.$store.state.now30s;
        },
        invalidPurchaseAge() {
            if (!this.course?.purchase_age) return false;
            if (this.canBookForOthers) {
                let minAge = Number(this.course.purchase_age.from);
                let maxAge = Number(this.course.purchase_age.to);
                return this.users
                    .filter(u => u.selected)
                    .some(u => {
                        if (typeof u.invalidPurchaseAge === 'function') return u.invalidPurchaseAge();
                        if (!u.birthdate) return true;
                        let age = Date.getAge(u.birthdate);
                        return age < minAge || age > maxAge;
                    });
            }
            if (!this.$store.state.isLoggedIn) return false;
            let user = this.$store.state.user;
            if (!user?.birthdate) return true;
            let age = Date.getAge(user.birthdate);
            return age < Number(this.course.purchase_age.from) || age > Number(this.course.purchase_age.to);
        },
        purchaseAgeTooYoung() {
            if (!this.course?.purchase_age || !this.$store.state.user?.birthdate) return false;
            return Date.getAge(this.$store.state.user.birthdate) < Number(this.course.purchase_age.from);
        },
        // True when the age requirement should block the Bevaka (watch/queue) button.
        // Watching is only blocked when the course is exclusively bookable for the
        // logged-in user themselves and they don't meet the age requirement.
        // If the course allows booking for others, watching is always permitted —
        // the user may be queueing on behalf of a qualifying family member.
        ageBlocksWatching() {
            if (!this.course?.purchase_age) return false;
            if (this.course.canBookFor?.includes('others')) return false;
            if (!this.$store.state.isLoggedIn) return false;
            const user = this.$store.state.user;
            if (!user?.birthdate) return true;
            const age = Date.getAge(user.birthdate);
            const maxAge = Number(this.course.purchase_age.to);
            return age < Number(this.course.purchase_age.from) || (!isNaN(maxAge) && age > maxAge);
        },
        canBook() {
            return this.course && this.course.firstBookingTime < this.$store.state.now30s && this.course.lastBookingTime > this.$store.state.now30s && this.course.spaceLeft !== 0 && !this.canOnlyBookSelfAndBooked && !this.isBlocked && !this.invalidPurchaseAge;
        },
        isBlocked() {
            return this.$store.state.user?.isBlocked('block_course');
        },
        canWatch() {
            if (!this.course || this.ageBlocksWatching) return false;
            const now = this.$store.state.now30s;
            // Booking window must not have closed yet (matches the backend watch/add check)
            if (this.course.lastBookingTime <= now) return false;
            // While booking is open you only watch a full course (otherwise you book).
            // Before booking opens you can always queue, regardless of free spots.
            const bookingOpen = this.course.firstBookingTime < now;
            return bookingOpen ? this.course.spaceLeft == 0 : true;
        },
        isLoggedIn() {
            return this.$store.state.isLoggedIn;
        },
        hasSms() {
            return this.$store.state.settings?.sms;
        },
        watchInfo() {
            let bookings = this.$store.state.bookings;
            return this.course && bookings?.coursewatches?.find(cw => cw.course_id == this.course.id);
        },
        isWatching() {
            return !!this.watchInfo;
        },
        bookableUsers() {
            if (!this.course) {
                return [];
            }
            if (this.course.canBookFor && !this.course.canBookFor.includes('self')) {
                return this.users.filter(x => !x.me);
            }
            if (this.course.canBookFor && !this.course.canBookFor.includes('others')) {
                return this.users.filter(x => x.me);
            }

            return this.users;
        },
        newUserPrimaryColor() {
            return this.bookableUsers.filter(x => x.selected).length == 0;
        },
        canBookForOthers() {
            return this.course?.canBookFor?.includes('others');
        },
        splitButtonUsers() {
            return this.bookableUsers
                .filter(u => !this.isBookedOrInCart(u) && !u.isBlocked() && !(typeof u.invalidPurchaseAge === 'function' && u.invalidPurchaseAge()))
                .map(u => ({
                    id: u.id,
                    name: u.name,
                    me: u.me,
                    canBook: true
                }));
        },
        courseStaffs() {
            if (!this.course) return null;
            if (this.course.staffs) return this.course.staffs;
            if (!this.course.workouts || this.course.workouts.length == 0) return null;
            let staffs = [];
            this.course.workouts.forEach(w => {
                w.staffs.forEach(s => {
                    if (!staffs.find(x => x.id == s.id)) staffs.push(s);
                })
            })
            return staffs;
        },
        cancellableBookings() {
            const myId = this.$store.state.user?.id;
            return this.bookings
                .filter(bu => this.canCancel(bu.booking))
                .sort((a, b) => {
                    if (a.booking.user_id === myId) return -1;
                    if (b.booking.user_id === myId) return 1;
                    return 0;
                });
        },
        bookingLabelItems() {
            if (!this.bookings || !this.bookings.length) return null;
            return this.bookings.map(bu => ({ id: bu.user.id, name: bu.user.name }));
        },
        formattedStaffs() {
            const list = this.courseStaffs || [];
            return list.map(s => ({
                ...s,
                displayName: formatStaffName(s, this.showLastnameForInstructors)
            }));
        }
    },
    methods: {
        closeWatchDialog() {
            this.showWatchDialog = false;
        },
        inc(product_id) {
            this.basket[product_id] += 1;
        },
        dec(product_id) {
            if (this.basket[product_id]) {
                this.basket[product_id] -= 1;
            }
        },
        stripHtml,
        showAdditionalProduct(product) {
            this.showingProduct = product;
            Vue.nextTick(() => {
                this.$refs.productDialog.show();
            })
        },
        pay() {
            this.$refs.checkout.completePurchase();
        },
        changeNumberOfItems(item, count) {
            if (item.user_id) {
                let user = this.users.filter(user => user.selected).find(user => user.id == item.user_id)
                if (user) {
                    let b = user.basket[item.product_id];
                    let bi = b?.find(i => i.variant == item.variant);
                    if (bi) {
                        bi.count = count;
                    }
                }
            } else if (item.users && item.users.length == 1) {
                let user = this.users.filter(user => user.selected).find(user => user.id == item.users[0]);
                if (user) {
                    if (count == 0) {
                        user.membership = null;
                        if (this.$booking.isMembershipStillRequired(this.checkoutItemsForMembershipCheckout)) {
                            this.currentStep = this.steps.findIndex(step => step.id == 'membership');
                        }
                    }
                }
            }
        },
        changeItemUsers(item, users) {
            // Only relevant for additional products that are training cards or memberships
            if (!item.product || !['trainingcard', 'membership'].includes(item.product.type)) {
                return;
            }

            // Find the user whose basket this additional product came from
            const ownerUser = this.users.find(u => u.id === item.user_id);
            if (!ownerUser || !ownerUser.basket) {
                return;
            }

            const basket = ownerUser.basket[item.product_id];
            if (!basket) {
                return;
            }

            const bi = basket.find(b => b.variant === item.variant);
            if (!bi) {
                return;
            }

            // Store the new assignment per card. CheckoutItems will pick this up via bi.users.
            bi.users = users.slice(0, bi.count);
        },
        // Goes through all users and sets questions unless set, basket unless set and handles reserving / unreserving
        updateUsers() {
            if (!this.course) {
                return;
            }

            this.users.forEach(user => {
                if (!user.questions) {
                    let questions = {};
                    this.course.questions?.forEach(q => {
                        q = Object.assign({}, q);
                        q.title = q.name;
                        q.value = null;
                        questions[q.name] = q;
                    })
                    this.$set(user, 'questions', questions)
                }

                if (!user.basket) {
                    this.$set(user, 'basket', {})
                    let a = this.course.additional_products || [];
                    a.forEach(id => this.$set(user.basket, id, []));
                }

                if (!user.variantsForIncludedProducts && this.products) {
                    let a = {};
                    this.includedProductsRequireVariant.forEach(x => a[x.id] = null);
                    this.$set(user, 'variantsForIncludedProducts', a)
                }

                if (user.selected && this.bookings.find(b => b.booking.user_id == user.id)) {
                    user.selected = false;
                }

                if (user.selected && !user.reservation && !user.reserving) {
                    this.reserve(user);
                }

                if (!user.selected && user.reservation) {
                    this.unreserve(user);
                }
            })

        },
        showAdduserDialog() {
            this.$store.commit('showAddUserDialog', {
                title: this.$translate('Add new participant'),
                booking_fields: true,
                callback: (data) => {
                    const invalidPurchaseAge = () => {
                        let pa = this.course?.purchase_age;
                        if (!pa) return false;
                        if (!data.birthdate) return true;
                        let age = Date.getAge(data.birthdate);
                        return age < Number(pa.from) || age > Number(pa.to);
                    };
                    let user = {
                        id: data.id,
                        name: getName(data),
                        selected: !invalidPurchaseAge(),
                        me: false,
                        reservation: null,
                        reserving: false,
                        expanded: true,
                        membership: null,
                        isBlocked: () => false,
                        invalidPurchaseAge
                    };
                    this.users.push(user);
                    this.updateUsers();
                }
            });
        },
        showChangeUserDialog(user) {
            this.$store.commit('showChangeUserDialog', {
                user_id: user.id
            });
        },
        reserve(user) {
            if (user.reserving || !this.showBookingDialog) {
                return;
            }
            user.reserving = true;
            let data = {course_id: this.course.id};
            this.$api.post('/api/resource/reservation/add', data).then(response => {
                user.reserving = false;
                if (response.ok) {
                    response.json().then(x => {
                        user.reservation = x.key;
                    });
                } else {
                    this.$store.commit('showErrorDialog', {title: this.$translate('Error during reservation'), text: this.$translate('Something went wrong when the booking was to be reserved')});
                    user.selected = false;
                }
            });
        },
        unreserve(user) {
            if (user.reserving || !user.reservation) {
                return;
            }
            user.reserving = true;
            this.$api.post('/api/resource/reservation/cancel', {key: user.reservation}).then(() => {
                user.reserving = false;
                user.reservation = null;
                user.selected = false;
            });
        },
        unreserveAll() {
            let rusers = this.users.filter(user => user.reservation);
            let keys = rusers.map(user => user.reservation);
            if (keys.length) {
                rusers.forEach(user => {
                    user.reserving = true;
                })
                // this.$api.post('/api/resource/reservation/cancel', {key: keys}).then(() => {
                //   rusers.forEach(user => {
                //       user.reserving = false;
                //       user.reservation = null;
                //       user.selected = false;
                //   })
                // });
                rusers.forEach(user => {
                    user.reserving = false;
                    user.reservation = null;
                    user.selected = false;
                })
            }
        },
        validateCourseForCart() {
            const conflictingSiteId = this.course.sites
                .find(siteId => !this.isSiteIdValidForCart(this.cartName, siteId))

            if (conflictingSiteId) {
                this.$store.dispatch('showInvalidCartDialog', {
                    cartName: this.cartName,
                    conflictingSiteId
                });
                return false;
            }
            return true;
        },
        book() {
            if (this.hasModule('MultipleLegalEntities') && !this.validateCourseForCart()) {
                return;
            }

            this.currentStep = (this.$store.state.isLoggedIn ? 1 : 0);
            this.showBookingDialog = true;
            this.updateUsers();

            try {
                let ecommerceTrackingItem = {
                    id: 'c' + this.course.id,
                    name: this.course.name,
                    price: this.course.price,
                    vat: this.course.vat,
                };
                this.$ecommercetracking.addToCart(ecommerceTrackingItem);
            } catch (error) {
                Sentry.captureException(error);
            }

        },
        clearAnswersAndBasket() {
            this.users.forEach(user => {
                user.questions = null;
                user.basket = null;
            })
            this.bookingId = getRandomId(8);
        },
        canCancel() {
            // Use the current course's cancel settings rather than the snapshot stored at
            // booking-creation time. The snapshot can be stale if the admin enables (or
            // changes) cancellation rules after existing bookings were made.
            const rules = this.cancelRules;
            if (!rules || !rules.canCancel) {
                return false;
            }
            // No deadline means cancellation is always allowed.
            if (!rules.lastCancelTime) {
                return true;
            }
            // cancelRules.lastCancelTime is already an absolute Date (computed in cancelRules).
            return this.$store.state.now30s < rules.lastCancelTime;
        },
        cancel(booking) {
            this.courseBookingToCancel = booking;
            this.showCancelBooking = true;
        },
        updateMemberInformationIfNeeded(cb) {
            let user = this.$store.state.user;
            let data = {};
            let props = ['firstname', 'lastname', 'mail'];
            if (this.hasSms) {
                props.push('phone');
            }
            let needUpdate = false;
            props.forEach(prop => {
                if (this.watch[prop].value != user[prop]) {
                    data[prop] = this.watch[prop].value;
                    needUpdate = true;
                }
            })

            if (needUpdate) {
                this.$api.post('/api/memberapi/change', data).then(cb);
            } else {
                cb();
            }
        },
        startWatching() {
            this.watch.loading = true;
            this.updateMemberInformationIfNeeded(() => {
                this.$api.post('/api/memberapi/watch/add', null, {course_id: this.course.id, mail: this.watch.sendMail, sms: this.hasSms && this.watch.sendSms}).then((response) => {
                    response.json().then(data => {
                        this.watch.loading = false;
                        if (response.ok) {
                            this.showWatchDialog = false;
                        } else {
                            let text = this.$translate(data.message);
                            this.$store.commit('showSnackBar', {text});
                        }
                    })
                } )
            });
        },
        stopWatching() {
            this.$api.post('/api/memberapi/watch/remove', null, {course_id: this.course.id});
            this.showWatchDialog = false;
        },
        getCourse() {
            if (!this.courseId) {
                return;
            }
            this.getCourseDebounce();
        },
        userSelectedClicked(user, selected) {
            user.expanded = selected;
            this.updateUsers();
        },
        getProductSubtitle(product) {
            if (product.variants && product.variants.length > 1) {
                return this.$translate('Choose between {0} variants').format(product.variants.length);
            } else if (product.trainingsessions) {
                const punches = this.$translate('Punch tickets').toLowerCase()
                return `${product.trainingsessions} ${punches}`;
            } else if (product.auto) {
                return this.$translate('Recurring, automatically extended')
            } else if (product.period && product.periodValidFrom && product.periodValidTo) {
                return `${this.$translate('Valid from')} ${Date.formatFullShort(product.periodValidFrom)} ${this.$translate('to')} ${Date.formatFullShort(product.periodValidTo)}`;
            } else if (product.validTime) {
                return `${this.$translate('Valid for')} ${product.validTime} ${this.$translate(product.validType + (product.validTime === 1 ? '' : 's')).toLowerCase()}`;
            }
        },
        onBuyMembership(product, user) {
            let u = this.users.find(u => u.id == user.id);
            u.membership = product;
            if (!this.$booking.isMembershipStillRequired(this.checkoutItemsForMembershipCheckout)) {
                this.currentStep = this.steps.findIndex(step => step.id == 'checkout');
            }
        },
        addToCart() {
            const cartName = this.cartName;
            this.checkoutItems.forEach(item => {
                this.$store.dispatch('addToCart', {cartName, site_id: item.site_id || this.selectedSiteId, product_id: item.product_id, course_id: item.course_id, price: item.price, count: item.count, answers: item.answers, reservation: item.reservation, variants_for_included_products: item.variants_for_included_products, user_id: item.user_id, users: item.users, tmp_id: item.tmp_id});
            })
            this.$store.dispatch('setCartVisibility', {cartName, showBottomBar: true, showCheckout: false});
            // Close dialog without removing reservations
            this.users.forEach(user => {
                user.reserving = false;
                user.reservation = null;
                user.selected = false;
            });
            this.showBookingDialog = false;
        },
        isBooked(user) {
            return !!this.bookings.find(bu => bu.booking.user_id == user.id)
        },
        isInCart(user) {
            if (this.useCart) {
                const items = getCartItems(this.cartName);
                return !!items.find(item => item.course_id == this.course.id && item.users && item.users.find(x => x == user.id));
            }

            return false;
        },
        isBookedOrInCart(user) {
            return this.isBooked(user) || this.isInCart(user);
        },
        reservationTimeout() {
            this.showBookingDialog = false;
        },
        bookFor(user) {
            const target = this.users.find(u => u.id === user.id);
            if (target) {
                this.users.forEach(u => { u.selected = false; });
                target.selected = true;
                this.bookForUserId = user.id;
                this.updateUsers();
            }
            this.book();
        },
        openWorkoutDialog(workout) {
            this.selectedWorkout = workout;
            this.showWorkoutDialog = true;
        },
        openMapDialog(resource) {
            this.showMap = resource;
            this.showMapDialog = true;
        },
        doShowStaffInfoDialog(staff) {
            this.showStaffInfoDialog = true;
            this.showStaff = staff;
        }
    },
    mounted() {
        window.addEventListener('beforeunload', this.unreserveAll);
        this.unsubscribe = this.$root.$api.subscribe({type: 'course', callback: (_, event) => {
            let course = event.data.find(course => course.id == this.courseId);
            if (course && this.course) {
                this.course.spaceLeft = course.spaceLeft;
            }
            this.getCourse();
        }});
    },
    beforeCreate() {
        this.getCourseDebounce = debounce(() => {
            // Debounce method "getCourseDebounce" is in "beforeCreate" beacuse it's used by "watch" which is called before "created/mounted".
            // Debounce method can't be created in "methods" during import, as each component must have its own debounce method.
            this.$api.get('/api/public/course/get', {id: this.courseId}).then(courses => {
                if (courses && courses.length == 1) {
                    let course = courses[0];
                    course.startTime = Date.newFull(course.startTime);
                    course.endTime = Date.newFull(course.endTime);
                    course.firstBookingTime = Date.newFull(course.firstBookingTime);
                    course.lastBookingTime = Date.newFull(course.lastBookingTime);
                    course.workouts?.forEach(w => this.$booking.transformWorkout(w));
                    this.course = course;
                    this.$emit('course-loaded', course);
                    // Fetch price-rule evaluated price for this course
                    const startDate = course.startTime ? course.startTime.yyyymmdd() : null;
                    this.fetchPriceRulePrice(null, null, startDate, course.id);
                } else {
                    this.courseLoadingFailed = true;
                    this.$emit('course-loaded', null);
                }
            }).catch(() => {
                this.courseLoadingFailed = true;
                this.$emit('course-loaded', null);
            })
        }, 500);
    },
    destroyed() {
        this.unreserveAll();
        window.removeEventListener('beforeunload', this.unreserveAll);
        if (this.unsubscribe) {
            this.unsubscribe();
        }
    }
}
</script>

<style scoped>
.included-products-list {
    display: flex;
    flex-direction: column;
    gap: 6px;
}

.included-products-list__panel {
    border-radius: 14px !important;
    overflow: hidden;
}

.included-products-list ::v-deep .v-expansion-panel {
    background-color: #F5F5F5 !important;
}

.included-products-list__header {
    min-height: 44px !important;
}

.included-products-list ::v-deep .v-expansion-panel-content__wrap {
    padding: 0 12px 12px;
}

/* Clickable header that collapses the occasions list (closed by default) so the
   Book button stays reachable for courses with many occasions. Styled to match
   the gray pill of the inner course-session-list panels. The list itself sits
   directly under it, so it keeps its own styling and full width — no need to
   reach into the inner component's internals. */
.occasions-toggle {
    min-height: 44px;
    padding: 0 12px;
    background-color: #F5F5F5;
    border-radius: 14px;
    cursor: pointer;
    user-select: none;
}
</style>
